import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import ResumePreview from '../components/ResumePreview';
import { TEMPLATES, CV_TEMPLATES, CL_TEMPLATES } from '../data/samples';
import {
  MarketingHeader, MarketingFooter, LandingStyles,
  ATS_FRIENDLY_TEMPLATE_IDS, buildShowcaseResume, buildShowcaseCV, buildShowcaseCL,
} from './OtherPages';
import {
  Search, Sparkles, FileText, Award, Mail, Star, ArrowRight, Check, CheckCircle2,
  ChevronDown, ChevronLeft, ChevronRight, X, Briefcase, Palette, Code2, HeartPulse, Home,
  Shield, TrendingUp, GraduationCap, Flower2, Grid3x3, SlidersHorizontal, History, Flame,
  Eye, Zap, Wand2, Quote, BookOpen, Gem, Square, ThumbsUp,
} from 'lucide-react';

// ── Data: student ids, industry taxonomy, style taxonomy ─────────────────────
const STUDENT_IDS = new Set(['campus_fresh', 'intern_spark', 'scholar_navy', 'fresh_start', 'campus_dark']);

const INDUSTRY_GROUPS = [
  { id: 'professional', label: 'Professional',           Icon: Briefcase,    accent: '#1D4ED8', badgeBg: '#EFF6FF' },
  { id: 'creative',     label: 'Creative & Design',       Icon: Palette,      accent: '#7C3AED', badgeBg: '#F5F3FF' },
  { id: 'technology',   label: 'Technology',              Icon: Code2,        accent: '#0891B2', badgeBg: '#ECFEFF' },
  { id: 'medical',      label: 'Medical & Healthcare',    Icon: HeartPulse,   accent: '#DC2626', badgeBg: '#FEF2F2' },
  { id: 'real_estate',  label: 'Real Estate',             Icon: Home,         accent: '#B45309', badgeBg: '#FFFBEB' },
  { id: 'security',     label: 'Security & Protective',   Icon: Shield,       accent: '#334155', badgeBg: '#F1F5F9' },
  { id: 'business',     label: 'Business & Management',   Icon: TrendingUp,   accent: '#059669', badgeBg: '#ECFDF5' },
  { id: 'beauty',       label: 'Beauty & Wellness',       Icon: Flower2,      accent: '#DB2777', badgeBg: '#FDF2F8' },
  { id: 'student',      label: 'Student & Internship',    Icon: GraduationCap,accent: '#0D9488', badgeBg: '#F0FDFA' },
];
const INDUSTRY_LOOKUP = { all: { id: 'all', label: 'All Templates', Icon: Grid3x3, accent: '#0F172A', badgeBg: '#F1F5F9' },
  ats: { id: 'ats', label: 'ATS Friendly', Icon: CheckCircle2, accent: '#1D4ED8', badgeBg: '#EFF6FF' } };
INDUSTRY_GROUPS.forEach(g => { INDUSTRY_LOOKUP[g.id] = g; });
const INDUSTRY_PILLS = [INDUSTRY_LOOKUP.all, INDUSTRY_LOOKUP.ats, ...INDUSTRY_GROUPS];

const TEMPLATE_INDUSTRY_MAP = {
  harvard_standard: 'professional', blue_analyst: 'professional', blue_corporate: 'professional',
  russian_classic: 'professional',  classic: 'professional',      academic: 'professional',
  tech: 'technology',
  modern: 'professional', executive: 'professional', minimal: 'professional', corporate: 'professional',
  elegant: 'professional', europass: 'professional', academic_modern: 'professional',
  gregory_sidebar: 'professional', slate_sidebar: 'professional', ivory_serif: 'professional',
  oxford_classic: 'professional', cambridge_blue: 'professional', stem_scholar: 'professional',
  emerald_academic: 'professional', cobalt_timeline: 'professional', manuscript: 'professional',
  nordic_scholar: 'professional', amber_executive: 'professional', bold_stripe: 'professional',
  forest_pro: 'professional', sky_pro: 'professional', champagne: 'professional',
  sage: 'professional', startup_clean: 'professional',
  creative: 'creative', editorial: 'creative', graphite: 'creative', navy_timeline: 'creative',
  crimson: 'creative', teal_banner: 'creative', violet_sidebar: 'creative', rose_gold: 'creative',
  charcoal_split: 'creative', terracotta: 'creative',
  mono_pro: 'technology', swiss_grid: 'technology',
  med_pro_blue: 'medical', clinical_teal: 'medical', nurse_rx: 'medical', healthcare_pro: 'medical', critical_care: 'medical',
  realty_pro: 'real_estate', estate_sidebar: 'real_estate', luxury_broker: 'real_estate', re_corporate: 'real_estate', property_exec: 'real_estate',
  shield_pro: 'security', patrol_blue: 'security', sentinel_dark: 'security', guardian_grid: 'security', tactical_clean: 'security',
  exec_suite: 'business', boardroom: 'business', bizdev_pro: 'business', strategy_blue: 'business', c_suite: 'business',
  bloom_sidebar: 'beauty', salon_chic: 'beauty', spa_zen: 'beauty', glow_pro: 'beauty', beauty_badge: 'beauty',
  campus_fresh: 'student', intern_spark: 'student', scholar_navy: 'student', fresh_start: 'student', campus_dark: 'student',
};
const getIndustry = (id) => TEMPLATE_INDUSTRY_MAP[id] || 'professional';

const STYLE_GROUPS = [
  { id: 'minimal',   label: 'Minimal',   desc: 'Clean lines, generous whitespace',    Icon: Square,   accent: '#059669', badgeBg: '#ECFDF5' },
  { id: 'modern',    label: 'Modern',    desc: 'Bold color, contemporary structure',  Icon: Sparkles, accent: '#EF1D28', badgeBg: '#FFF1F2' },
  { id: 'classic',   label: 'Classic',   desc: 'Timeless, traditional layouts',       Icon: BookOpen, accent: '#1F2937', badgeBg: '#F1F5F9' },
  { id: 'executive', label: 'Executive', desc: 'Authoritative, leadership-ready',     Icon: Award,    accent: '#B45309', badgeBg: '#FFFBEB' },
  { id: 'creative',  label: 'Creative',  desc: 'Expressive looks for standout roles', Icon: Palette,  accent: '#7C3AED', badgeBg: '#F5F3FF' },
  { id: 'elegant',   label: 'Elegant',   desc: 'Refined serif type, luxury accents',  Icon: Gem,      accent: '#A37E2C', badgeBg: '#FEFCE8' },
];
const TEMPLATE_STYLE_MAP = {
  modern: 'modern', classic: 'classic', executive: 'executive', minimal: 'minimal', creative: 'creative',
  tech: 'modern', elegant: 'elegant', corporate: 'classic', blue_corporate: 'classic', navy_timeline: 'modern',
  crimson: 'modern', harvard_standard: 'classic', blue_analyst: 'classic', russian_classic: 'classic',
  gregory_sidebar: 'modern', slate_sidebar: 'modern', mono_pro: 'minimal', ivory_serif: 'elegant',
  teal_banner: 'modern', swiss_grid: 'minimal', amber_executive: 'executive', violet_sidebar: 'creative',
  bold_stripe: 'modern', rose_gold: 'elegant', forest_pro: 'classic', sky_pro: 'modern',
  charcoal_split: 'executive', terracotta: 'creative', startup_clean: 'modern', champagne: 'elegant',
  editorial: 'creative', sage: 'minimal', graphite: 'creative', med_pro_blue: 'classic',
  clinical_teal: 'modern', nurse_rx: 'classic', healthcare_pro: 'executive', critical_care: 'executive',
  realty_pro: 'classic', estate_sidebar: 'modern', luxury_broker: 'elegant', re_corporate: 'executive',
  property_exec: 'executive', shield_pro: 'classic', patrol_blue: 'classic', sentinel_dark: 'executive',
  guardian_grid: 'modern', tactical_clean: 'minimal', exec_suite: 'executive', boardroom: 'executive',
  bizdev_pro: 'modern', strategy_blue: 'classic', c_suite: 'elegant', bloom_sidebar: 'creative',
  salon_chic: 'elegant', spa_zen: 'minimal', glow_pro: 'creative', beauty_badge: 'creative',
  campus_fresh: 'modern', intern_spark: 'creative', scholar_navy: 'classic', fresh_start: 'minimal', campus_dark: 'modern',
};
const getStyle = (id) => TEMPLATE_STYLE_MAP[id] || 'modern';

const SORT_OPTIONS = [
  { id: 'popular', label: 'Most Popular' },
  { id: 'newest',  label: 'Newest First' },
  { id: 'az',      label: 'A – Z' },
  { id: 'ats',     label: 'ATS Score' },
];
const sortTemplates = (pool, sortBy, docType) => {
  const arr = [...pool];
  if (sortBy === 'az') arr.sort((a, b) => a.name.localeCompare(b.name));
  else if (sortBy === 'newest') arr.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
  else if (sortBy === 'ats') arr.sort((a, b) => {
    const as = docType === 'resume' && ATS_FRIENDLY_TEMPLATE_IDS.has(a.id) ? 1 : 0;
    const bs = docType === 'resume' && ATS_FRIENDLY_TEMPLATE_IDS.has(b.id) ? 1 : 0;
    return bs - as;
  });
  return arr;
};

const FEATURED_IDS = {
  resumes: ['harvard_standard', 'blue_corporate', 'modern'],
  cvs: ['academic_modern', 'oxford_classic', 'cambridge_blue'],
  'cover-letters': ['cl_modern', 'cl_professional', 'cl_executive'],
};
const EDITORS_PICK_IDS = {
  resumes: ['executive', 'minimal', 'tech', 'creative', 'elegant', 'swiss_grid'],
  cvs: ['stem_scholar', 'emerald_academic', 'nordic_scholar', 'manuscript'],
  'cover-letters': ['cl_bold', 'cl_elegant', 'cl_minimal', 'cl_compact'],
};

const TRENDING_SEARCHES = ['Software Engineer', 'Marketing Manager', 'Nurse Resume', 'Minimalist', 'Executive', 'ATS Friendly', 'Creative Portfolio', 'Student Resume'];
const AI_SUGGESTIONS = [
  'ATS-friendly marketing resume', 'Modern minimalist for tech', 'Executive leadership resume',
  'Creative portfolio design', 'Clean resume for healthcare', 'Two-column resume with photo',
];
const RECOMMEND_KEYWORDS = [
  { match: /(engineer|tech|developer|software)/i, industry: 'technology' },
  { match: /(nurse|medical|health|clinical)/i,     industry: 'medical' },
  { match: /(creative|design|portfolio|art)/i,     industry: 'creative' },
  { match: /(student|intern|campus)/i,             industry: 'student' },
  { match: /(real estate|realty|property)/i,        industry: 'real_estate' },
  { match: /(business|manager|executive|strategy)/i, industry: 'business' },
  { match: /(security|guard|patrol)/i,             industry: 'security' },
  { match: /(beauty|salon|spa|wellness)/i,         industry: 'beauty' },
];
const tokenize = (s) => s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const matchesWords = (haystackWords, queryWords) => queryWords.some(qw => haystackWords.some(hw => hw.includes(qw) || qw.includes(hw)));
const getSmartRecommendations = (term) => {
  if (term) {
    const hit = RECOMMEND_KEYWORDS.find(k => k.match.test(term));
    if (hit) {
      const pool = TEMPLATES.filter(t => getIndustry(t.id) === hit.industry);
      if (pool.length) return { list: pool.slice(0, 3), basis: `Because you searched “${term}”` };
    }
  }
  const fallback = ['harvard_standard', 'minimal', 'sky_pro'].map(id => TEMPLATES.find(t => t.id === id)).filter(Boolean);
  return { list: fallback, basis: 'Trending picks this week' };
};

const WHY_CHOOSE_ITEMS = [
  { Icon: CheckCircle2, title: 'ATS-Optimized', desc: 'Engineered to pass real applicant tracking systems, not just look good on screen.' },
  { Icon: Wand2, title: 'Designed by Experts', desc: 'Layouts refined with hiring managers and recruiters across thousands of resumes.' },
  { Icon: SlidersHorizontal, title: 'Fully Customizable', desc: 'Swap colors, fonts and sections in seconds inside the builder — no design skills needed.' },
  { Icon: Zap, title: 'Free Forever', desc: 'Every template on this page is free to use, edit, and export. No paywalls, no surprises.' },
];

const TESTIMONIALS = [
  { quote: 'I found an ATS-friendly template in two minutes and had interviews within a week.', name: 'Priya S.', role: 'Product Manager', initials: 'PS', color: '#EF1D28' },
  { quote: 'The layouts feel like something I’d pay a designer for. My resume finally looks like me.', name: 'Daniel K.', role: 'UX Designer', initials: 'DK', color: '#7C3AED' },
  { quote: 'Switching templates took one click and reformatted everything instantly. Huge time saver.', name: 'Amara O.', role: 'Business Analyst', initials: 'AO', color: '#059669' },
];

const FAQ_ITEMS = [
  { q: 'Are all templates really free?', a: 'Yes. Every resume, CV, and cover letter template on this page is completely free to use, edit, and download — no hidden tiers.' },
  { q: 'Which templates are ATS-friendly?', a: 'Look for the blue “ATS Friendly” badge on any card. Those layouts are tested against real applicant tracking systems to make sure your content parses correctly.' },
  { q: 'Can I switch templates after I start?', a: 'Absolutely. Change templates any time from inside the builder without losing your content — everything flows straight into the new layout.' },
  { q: 'What file formats can I export?', a: 'Every template exports to PDF and Word (.docx), ready to attach to any application or job board.' },
  { q: 'Do I need design experience to customize a template?', a: 'No. Colors, fonts, and section order are all adjustable with simple controls right in the builder — no design skills required.' },
];

// ── Small shared pieces ───────────────────────────────────────────────────────
const SectionHeading = ({ title, subtitle }) => (
  <div className="tplx-sec-heading tplx-sec-heading-center">
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const StarRow = ({ n = 5, size = 13 }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} fill={i < n ? '#FBBF24' : 'none'} color={i < n ? '#FBBF24' : '#D1D5DB'} strokeWidth={1.5} />
    ))}
  </div>
);

const TemplateCard = ({ tmpl, docType, buildFn, useTemplate, index = 0, color, onPreview, size = 'md', ribbon }) => {
  const preview = buildFn(tmpl);
  const isAts = docType === 'resume' && ATS_FRIENDLY_TEMPLATE_IDS.has(tmpl.id);
  const isStudent = docType === 'resume' && STUDENT_IDS.has(tmpl.id);
  const accent = color || tmpl.color || '#EF1D28';
  return (
    <div className={`tplx-card${size === 'lg' ? ' tplx-card-lg' : ''}`}>
      <div className="tplx-card-preview">
        <div className="tplx-card-badges">
          {ribbon && <span className="tplx-badge tplx-badge-ribbon"><Star size={10} fill="currentColor" /> {ribbon}</span>}
          {isAts && <span className="tplx-badge tplx-badge-ats"><CheckCircle2 size={10} /> ATS Friendly</span>}
          {!isAts && isStudent && <span className="tplx-badge tplx-badge-student">🎓 Student</span>}
          {!ribbon && !isAts && tmpl.isNew && <span className="tplx-badge tplx-badge-new">New</span>}
        </div>
        <div className="tplx-card-frame">
          <ResumePreview data={preview} id={`tplx-${docType}-${tmpl.id}-${index}`} singlePage />
        </div>
        <div className="tplx-card-fade" />
        <div className="tplx-card-overlay">
          <button type="button" className="tplx-card-btn tplx-card-btn-ghost" onClick={() => onPreview?.({ tmpl, docType, buildFn, color: accent })}>
            <Eye size={14} /> Preview
          </button>
          <button type="button" className="tplx-card-btn tplx-card-btn-solid" style={{ background: accent }} onClick={() => useTemplate(docType, tmpl.id)}>
            Use Template <ArrowRight size={14} />
          </button>
        </div>
      </div>
      <div className="tplx-card-body">
        <div className="tplx-card-title-row">
          <span className="tplx-card-title">{tmpl.name}</span>
          <span className="tplx-card-dot" style={{ background: accent }} />
        </div>
        <p className="tplx-card-desc">{tmpl.desc || `A polished ${docType.replace('_', ' ')} layout.`}</p>
      </div>
    </div>
  );
};

const CardGrid = ({ templates, docType, buildFn, useTemplate, onPreview, color, stagger = true }) => {
  const content = (
    <div className="tplx-grid">
      {templates.map((tmpl, i) => (
        stagger ? (
          <motion.div key={tmpl.id} variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }} transition={{ duration: 0.4, ease: 'easeOut' }}>
            <TemplateCard tmpl={tmpl} docType={docType} buildFn={buildFn} useTemplate={useTemplate} index={i} onPreview={onPreview} color={color} />
          </motion.div>
        ) : (
          <TemplateCard key={tmpl.id} tmpl={tmpl} docType={docType} buildFn={buildFn} useTemplate={useTemplate} index={i} onPreview={onPreview} color={color} />
        )
      ))}
    </div>
  );
  if (!stagger) return content;
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}>
      {content}
    </motion.div>
  );
};

const EmptyState = ({ onClear }) => (
  <div className="tplx-empty">
    <div className="tplx-empty-icon"><Search size={22} /></div>
    <div className="tplx-empty-title">No templates match your filters</div>
    <p>Try a different search term or clear your filters to see everything.</p>
    <button type="button" className="tplx-btn-secondary" onClick={onClear}>Clear filters</button>
  </div>
);

// ── Hero ───────────────────────────────────────────────────────────────────────
const HERO_PREVIEW_IDS = ['harvard_standard', 'modern', 'executive'];
const HeroSection = ({ user, navigate, onBrowse }) => (
  <section className="tplx-hero">
    <div className="tplx-hero-grid-bg" />
    <div className="tplx-hero-glow" />
    <div className="tplx-hero-inner">
      <div className="tplx-hero-left">
        <h1>Templates that make hiring<br />managers stop scrolling.</h1>
        <p>Every layout is ATS-tested, professionally designed, and 100% free — pick one and your details flow straight in.</p>
        <div className="tplx-hero-ctas">
          {user ? (
            <button type="button" className="tplx-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
          ) : (
            <button type="button" className="tplx-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Building Free</button>
          )}
          <button type="button" className="tplx-btn-ghost-dark" onClick={onBrowse}>Browse Templates</button>
        </div>
      </div>
      <div className="tplx-hero-right">
        <div className="tplx-hero-stack">
          {HERO_PREVIEW_IDS.map((id, i) => {
            const tmpl = TEMPLATES.find(t => t.id === id);
            if (!tmpl) return null;
            const preview = buildShowcaseResume(tmpl);
            return (
              <motion.div key={id} className={`tplx-hero-card-wrap tplx-hero-card-wrap-${i}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: i * 0.12 }}>
                <div className={`tplx-hero-card tplx-hero-card-${i}`}>
                  <div className="tplx-hero-card-chrome"><span /><span /><span /></div>
                  <div className="tplx-hero-card-preview">
                    <div style={{ width: 520, transform: 'scale(0.4)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                      <ResumePreview data={preview} id={`hero-${id}`} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
          <motion.div className="tplx-float-badge tplx-float-badge-1" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
            <span className="tplx-float-icon tplx-float-icon-green"><CheckCircle2 size={15} /></span>
            <span>ATS Friendly</span>
          </motion.div>
          <motion.div className="tplx-float-badge tplx-float-badge-2" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.65 }}>
            <span className="tplx-float-icon tplx-float-icon-blue"><ThumbsUp size={15} /></span>
            <span>HR Approved</span>
          </motion.div>
          <motion.div className="tplx-float-badge tplx-float-badge-3" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
            <span className="tplx-float-icon tplx-float-icon-purple"><Sparkles size={15} /></span>
            <span>Modern Design</span>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

// ── Search ─────────────────────────────────────────────────────────────────────
const SearchSection = ({ docTypes, activeDocId, onSelectDoc, query, setQuery, onSubmit, recent, onClearRecent, onPickTerm }) => {
  const [focused, setFocused] = useState(false);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSuggestionIdx(i => (i + 1) % AI_SUGGESTIONS.length), 3400);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="tplx-search-outer">
      <div className="tplx-search-card">
        <div className="tplx-doctype-tabs">
          {docTypes.map(d => (
            <button key={d.id} type="button" className={`tplx-doctype-tab${activeDocId === d.id ? ' active' : ''}`}
              style={activeDocId === d.id ? { background: d.color, borderColor: d.color } : undefined}
              onClick={() => onSelectDoc(d.id)}>
              <d.Icon size={14} /> {d.label}
            </button>
          ))}
        </div>

        <div className={`tplx-search-bar${focused ? ' focused' : ''}`}>
          <Search size={18} className="tplx-search-icon" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={e => { if (e.key === 'Enter') onSubmit(query); }}
            placeholder='Search templates — try "modern minimalist"...'
          />
          {query && <button type="button" className="tplx-search-clear" onClick={() => setQuery('')}><X size={14} /></button>}
        </div>

        <AnimatePresence mode="wait">
          <motion.button key={suggestionIdx} type="button" className="tplx-ai-suggestion"
            onClick={() => { setQuery(AI_SUGGESTIONS[suggestionIdx]); onSubmit(AI_SUGGESTIONS[suggestionIdx]); }}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3 }}>
            <Sparkles size={13} /> AI suggests: “{AI_SUGGESTIONS[suggestionIdx]}”
          </motion.button>
        </AnimatePresence>

        <div className="tplx-search-meta">
          <div className="tplx-search-block">
            <div className="tplx-search-label"><Flame size={12} /> Trending</div>
            <div className="tplx-search-chips">
              {TRENDING_SEARCHES.map(t => (
                <button key={t} type="button" className="tplx-chip" onClick={() => onPickTerm(t)}>{t}</button>
              ))}
            </div>
          </div>
          {recent.length > 0 && (
            <div className="tplx-search-block">
              <div className="tplx-search-label"><History size={12} /> Recent</div>
              <div className="tplx-search-chips">
                {recent.map(t => (
                  <button key={t} type="button" className="tplx-chip tplx-chip-recent" onClick={() => onPickTerm(t)}>{t}</button>
                ))}
                <button type="button" className="tplx-chip tplx-chip-clear" onClick={onClearRecent}>Clear</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ── AI recommendation banner ────────────────────────────────────────────────────
const AIRecommendationBanner = ({ recommended, basis, useTemplate }) => {
  if (!recommended.length) return null;
  return (
    <section className="tplx-ai-banner-outer">
      <motion.div className="tplx-ai-banner" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4 }}>
        <div className="tplx-ai-banner-head">
          <span className="tplx-ai-banner-icon"><Wand2 size={16} /></span>
          <div>
            <div className="tplx-ai-banner-title">Recommended for you</div>
            <div className="tplx-ai-banner-sub">{basis}</div>
          </div>
        </div>
        <div className="tplx-ai-banner-row">
          {recommended.map(tmpl => (
            <button key={tmpl.id} type="button" className="tplx-ai-banner-chip" onClick={() => useTemplate('resume', tmpl.id)}>
              <span className="tplx-ai-banner-dot" style={{ background: tmpl.color }} /> {tmpl.name} <ArrowRight size={12} />
            </button>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

// ── Category pills (industry) with sliding active bg ───────────────────────────
const CategoryPills = ({ active, onSelect, getCount }) => (
  <div className="tplx-pills-scroll">
    <div className="tplx-pills-row">
      {INDUSTRY_PILLS.map(g => {
        const isActive = active === g.id;
        return (
          <button key={g.id} type="button" className={`tplx-pill${isActive ? ' active' : ''}`} onClick={() => onSelect(g.id)}>
            {isActive && (
              <motion.span layoutId="tplxPillBg" className="tplx-pill-bg" style={{ background: g.accent }}
                transition={{ type: 'spring', stiffness: 420, damping: 34 }} />
            )}
            <span className="tplx-pill-content">
              <g.Icon size={14} /> {g.label}
              <span className="tplx-pill-count">{getCount(g.id)}</span>
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

// ── Browse by style ──────────────────────────────────────────────────────────
const StyleBrowseSection = ({ activeStyle, onSelect, counts }) => (
  <section className="tplx-section">
    <SectionHeading title="Browse by Style" subtitle="Pick the aesthetic that fits how you want to be seen." />
    <div className="tplx-style-grid">
      {STYLE_GROUPS.map(s => (
        <motion.button key={s.id} type="button" className={`tplx-style-card${activeStyle === s.id ? ' active' : ''}`}
          onClick={() => onSelect(activeStyle === s.id ? null : s.id)}
          whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 320, damping: 22 }}>
          <div className="tplx-style-icon" style={{ background: s.badgeBg, color: s.accent }}><s.Icon size={20} /></div>
          <div className="tplx-style-name">{s.label}</div>
          <div className="tplx-style-desc">{s.desc}</div>
          <div className="tplx-style-count">{counts[s.id] || 0} templates</div>
        </motion.button>
      ))}
    </div>
  </section>
);

// ── Featured templates ────────────────────────────────────────────────────────
const FeaturedSection = ({ docType, ids, pool, buildFn, useTemplate, onPreview, color }) => {
  const templates = ids.map(id => pool.find(t => t.id === id)).filter(Boolean);
  if (!templates.length) return null;
  return (
    <section className="tplx-section">
      <SectionHeading title="Featured Templates" subtitle="Our most-used, highest-converting layouts — start here if you're not sure." />
      <div className="tplx-grid tplx-grid-featured">
        {templates.map((tmpl, i) => (
          <TemplateCard key={tmpl.id} tmpl={tmpl} docType={docType} buildFn={buildFn} useTemplate={useTemplate} index={i} onPreview={onPreview} color={color} size="lg" ribbon="Featured" />
        ))}
      </div>
    </section>
  );
};

// ── Editor's picks (horizontal scroll) ──────────────────────────────────────────
const EditorsPicksSection = ({ docType, ids, pool, buildFn, useTemplate, onPreview, color }) => {
  const scrollRef = useRef(null);
  const templates = ids.map(id => pool.find(t => t.id === id)).filter(Boolean);
  if (!templates.length) return null;
  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  return (
    <section className="tplx-section tplx-section-tint">
      <SectionHeading title="Loved by our editors" subtitle="Trend-forward layouts our team keeps coming back to." />
      <div className="tplx-scroller-wrap">
        <button type="button" className="tplx-scroller-nav tplx-scroller-nav-left" onClick={() => scroll(-1)} aria-label="Scroll left"><ChevronLeft size={18} /></button>
        <div className="tplx-scroller-track" ref={scrollRef}>
          {templates.map((tmpl, i) => (
            <div key={tmpl.id} className="tplx-scroller-item">
              <TemplateCard tmpl={tmpl} docType={docType} buildFn={buildFn} useTemplate={useTemplate} index={i} onPreview={onPreview} color={color} />
            </div>
          ))}
        </div>
        <button type="button" className="tplx-scroller-nav tplx-scroller-nav-right" onClick={() => scroll(1)} aria-label="Scroll right"><ChevronRight size={18} /></button>
      </div>
    </section>
  );
};

// ── Industry section (3 shown + lazy view-all) ──────────────────────────────────
const IndustrySection = ({ group, allTemplates, expanded, onExpand, docType, buildFn, useTemplate, onPreview }) => {
  const groupTemplates = allTemplates.filter(t => getIndustry(t.id) === group.id);
  if (!groupTemplates.length) return null;
  const shown = expanded ? groupTemplates : groupTemplates.slice(0, 3);
  return (
    <section className="tplx-section">
      <div className="tplx-industry-head">
        <div className="tplx-industry-head-left">
          <span className="tplx-industry-icon" style={{ background: group.badgeBg, color: group.accent }}><group.Icon size={18} /></span>
          <div>
            <h3>{group.label}</h3>
            <span>{groupTemplates.length} templates</span>
          </div>
        </div>
        {!expanded && groupTemplates.length > 3 && (
          <button type="button" className="tplx-view-all" onClick={onExpand}>View All <ChevronRight size={14} /></button>
        )}
      </div>
      <CardGrid templates={shown} docType={docType} buildFn={buildFn} useTemplate={useTemplate} onPreview={onPreview} />
    </section>
  );
};

// ── Why choose ─────────────────────────────────────────────────────────────────
const WhyChooseSection = () => (
  <section className="tplx-section tplx-section-tint">
    <SectionHeading title="Why Choose Our Templates" subtitle="Built for one goal: getting your resume past the filters and in front of a human." />
    <div className="tplx-why-grid">
      {WHY_CHOOSE_ITEMS.map(item => (
        <motion.div key={item.title} className="tplx-why-card" whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }}>
          <div className="tplx-why-icon"><item.Icon size={20} /></div>
          <div className="tplx-why-title">{item.title}</div>
          <p>{item.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

// ── Testimonials ───────────────────────────────────────────────────────────────
const TestimonialsSection = () => (
  <section className="tplx-section">
    <SectionHeading title="What people are saying" />
    <div className="tplx-testimonial-grid">
      {TESTIMONIALS.map(t => (
        <motion.div key={t.name} className="tplx-testimonial" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4 }}>
          <Quote size={22} className="tplx-testimonial-quote" />
          <StarRow />
          <p>{t.quote}</p>
          <div className="tplx-testimonial-person">
            <span className="tplx-testimonial-avatar" style={{ background: t.color }}>{t.initials}</span>
            <div>
              <div className="tplx-testimonial-name">{t.name}</div>
              <div className="tplx-testimonial-role">{t.role}</div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="tplx-section">
      <SectionHeading title="Frequently Asked Questions" />
      <div className="tplx-faq-list">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={item.q} className={`tplx-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="tplx-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{item.q}</span>
                <span className="tplx-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="tplx-faq-answer">{item.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── Final CTA ──────────────────────────────────────────────────────────────────
const FinalCTASection = ({ user, navigate, onBrowse }) => (
  <section className="tplx-cta-outer">
    <div className="tplx-cta-card">
      <div className="tplx-cta-mesh" />
      <div className="tplx-cta-glow-1" />
      <div className="tplx-cta-glow-2" />
      <div className="tplx-cta-content">
        <h2>Your next resume starts<br />with the right template.</h2>
        <p>Pick a layout, drop in your details, and export a polished resume in minutes — completely free.</p>
        <div className="tplx-cta-actions">
          {user ? (
            <button type="button" className="tplx-btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard <ArrowRight size={16} /></button>
          ) : (
            <button type="button" className="tplx-btn-primary" onClick={() => navigate('/build/resume')}><Sparkles size={16} /> Start Building Free</button>
          )}
          <button type="button" className="tplx-btn-ghost-dark" onClick={onBrowse}>Browse Templates</button>
        </div>
        <div className="tplx-cta-perks">
          {['Free forever', 'No credit card', 'PDF + Word export'].map(t => (
            <span key={t}><Check size={12} strokeWidth={3} /> {t}</span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ── Preview modal ──────────────────────────────────────────────────────────────
const PreviewModal = ({ item, onClose, useTemplate }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!item) return null;
  const { tmpl, docType, buildFn, color } = item;
  const preview = buildFn(tmpl);
  return (
    <motion.div className="tplx-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="tplx-modal-card" onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}>
        <button type="button" className="tplx-modal-close" onClick={onClose} aria-label="Close preview"><X size={18} /></button>
        <div className="tplx-modal-preview">
          <div style={{ width: 794, transformOrigin: 'top center' }} className="tplx-modal-preview-inner">
            <ResumePreview data={preview} id={`modal-${tmpl.id}`} />
          </div>
        </div>
        <div className="tplx-modal-footer">
          <div>
            <div className="tplx-modal-name">{tmpl.name}</div>
            <div className="tplx-modal-desc">{tmpl.desc}</div>
          </div>
          <button type="button" className="tplx-modal-use" style={{ background: color }} onClick={() => { useTemplate(docType, tmpl.id); onClose(); }}>
            Use This Template <ArrowRight size={15} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
export const TemplateGalleryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  usePageSEO({
    title: 'Professional Resume Templates for Every Career | MarsResume',
    description: 'Explore modern resume templates for students, professionals, and executives. Find the perfect design, customize it online, and create a standout resume with MarsResume.',
    keywords: 'professional resume templates, modern resume templates, resume layouts, CV templates, creative resume templates, executive resume templates, resume examples',
    ogDescription: 'Discover recruiter-approved resume templates for every industry. Build a polished, professional resume faster with customizable designs.',
    ogImageAlt: 'Professional Resume Templates by MarsResume',
    twitterDescription: 'Browse elegant and ATS-compatible resume templates to help you create a resume that stands out and gets interviews.',
    robots: 'index, follow',
    canonical: '/templates',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const useTemplate = (type, templateId) => {
    if (user) navigate(`/builder/${type}/new?template=${templateId}`);
    else navigate(`/build/${type}?template=${templateId}`);
  };

  const DOC_TYPES = [
    { id: 'resumes', type: 'resume', label: 'Resumes', Icon: FileText, color: '#EF1D28', pool: TEMPLATES, build: buildShowcaseResume },
    { id: 'cvs', type: 'cv', label: 'CVs', Icon: Award, color: '#FB923C', pool: CV_TEMPLATES, build: buildShowcaseCV },
    { id: 'cover-letters', type: 'cover_letter', label: 'Cover Letters', Icon: Mail, color: '#059669', pool: CL_TEMPLATES, build: buildShowcaseCL },
  ];
  const getDocId = (id) => DOC_TYPES.some(d => d.id === id) ? id : 'resumes';

  const [activeDocId, setActiveDocId] = useState(() => getDocId(window.location.hash.replace('#', '')));
  useEffect(() => { setActiveDocId(getDocId(location.hash.replace('#', ''))); }, [location.hash]);
  const activeDoc = DOC_TYPES.find(d => d.id === activeDocId) || DOC_TYPES[0];

  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndustry, setActiveIndustry] = useState('all');
  const [activeStyle, setActiveStyle] = useState(null);
  const [sortBy, setSortBy] = useState('popular');
  const [expandedIndustries, setExpandedIndustries] = useState({});
  const [visibleCount, setVisibleCount] = useState(12);
  const [previewItem, setPreviewItem] = useState(null);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mrs_templates_recent_searches') || '[]'); } catch { return []; }
  });

  const gridRef = useRef(null);
  const scrollToGrid = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const selectDoc = (id) => {
    setActiveDocId(id);
    navigate(`/templates#${id}`, { replace: true });
    setActiveIndustry('all');
    setActiveStyle(null);
    setSearchQuery('');
  };

  const commitSearch = (q) => {
    const term = q.trim();
    if (!term) return;
    setRecentSearches(prev => {
      const next = [term, ...prev.filter(t => t.toLowerCase() !== term.toLowerCase())].slice(0, 6);
      try { localStorage.setItem('mrs_templates_recent_searches', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    scrollToGrid();
  };
  const clearRecent = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('mrs_templates_recent_searches'); } catch { /* ignore */ }
  };
  const pickTerm = (term) => { setSearchQuery(term); commitSearch(term); };

  useEffect(() => { setVisibleCount(12); }, [activeDocId, activeIndustry, activeStyle, searchQuery, sortBy]);

  const getIndustryCount = (id) => {
    if (id === 'all') return TEMPLATES.length;
    if (id === 'ats') return TEMPLATES.filter(t => ATS_FRIENDLY_TEMPLATE_IDS.has(t.id)).length;
    return TEMPLATES.filter(t => getIndustry(t.id) === id).length;
  };
  const styleCounts = useMemo(() => STYLE_GROUPS.reduce((acc, s) => {
    acc[s.id] = TEMPLATES.filter(t => getStyle(t.id) === s.id).length;
    return acc;
  }, {}), []);

  const filteredTemplates = useMemo(() => {
    let pool = activeDoc.pool;
    if (activeDocId === 'resumes') {
      if (activeIndustry !== 'all') {
        pool = activeIndustry === 'ats'
          ? pool.filter(t => ATS_FRIENDLY_TEMPLATE_IDS.has(t.id))
          : pool.filter(t => getIndustry(t.id) === activeIndustry);
      }
      if (activeStyle) pool = pool.filter(t => getStyle(t.id) === activeStyle);
    }
    const preTextPool = pool;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const queryWords = tokenize(q);
      let textFiltered = preTextPool.filter(t => {
        const industryLabel = activeDocId === 'resumes' ? (INDUSTRY_LOOKUP[getIndustry(t.id)]?.label || '') : '';
        const styleLabel = activeDocId === 'resumes' ? (STYLE_GROUPS.find(s => s.id === getStyle(t.id))?.label || '') : '';
        const haystackWords = tokenize(`${t.name} ${t.desc || ''} ${industryLabel} ${styleLabel}`);
        return matchesWords(haystackWords, queryWords);
      });
      // Fallback: no literal match — try ATS / industry-synonym matching (same
      // heuristic as the AI recommendation banner) so curated search chips
      // like "Software Engineer" still surface relevant resume templates.
      if (!textFiltered.length && activeDocId === 'resumes') {
        if (/\bats\b/.test(q)) {
          textFiltered = preTextPool.filter(t => ATS_FRIENDLY_TEMPLATE_IDS.has(t.id));
        } else {
          const hit = RECOMMEND_KEYWORDS.find(k => k.match.test(q));
          if (hit) textFiltered = preTextPool.filter(t => getIndustry(t.id) === hit.industry);
        }
      }
      pool = textFiltered;
    }
    return sortTemplates(pool, sortBy, activeDoc.type);
  }, [activeDoc, activeDocId, activeIndustry, activeStyle, searchQuery, sortBy]);

  const showResults = activeDocId !== 'resumes' || activeIndustry !== 'all' || !!activeStyle || !!searchQuery.trim();
  const clearFilters = () => { setActiveIndustry('all'); setActiveStyle(null); setSearchQuery(''); };

  const resultsTitle = searchQuery.trim() ? `Results for “${searchQuery.trim()}”`
    : activeStyle ? `${STYLE_GROUPS.find(s => s.id === activeStyle)?.label} Templates`
      : activeIndustry !== 'all' ? `${INDUSTRY_LOOKUP[activeIndustry]?.label} Templates`
        : `All ${activeDoc.label}`;

  const smartRec = useMemo(() => getSmartRecommendations(recentSearches[0] || searchQuery), [recentSearches, searchQuery]);

  return (
    <div className="tplx-page">
      <LandingStyles />
      <TemplatesPageStyles />
      <MarketingHeader />

      <HeroSection user={user} navigate={navigate} onBrowse={scrollToGrid} />

      <SearchSection
        docTypes={DOC_TYPES}
        activeDocId={activeDocId}
        onSelectDoc={selectDoc}
        query={searchQuery}
        setQuery={setSearchQuery}
        onSubmit={commitSearch}
        recent={recentSearches}
        onClearRecent={clearRecent}
        onPickTerm={pickTerm}
      />

      {activeDocId === 'resumes' && (
        <AIRecommendationBanner recommended={smartRec.list} basis={smartRec.basis} useTemplate={useTemplate} />
      )}

      <FeaturedSection docType={activeDoc.type} ids={FEATURED_IDS[activeDocId] || []} pool={activeDoc.pool} buildFn={activeDoc.build} useTemplate={useTemplate} onPreview={setPreviewItem} color={activeDoc.color} />

      <EditorsPicksSection docType={activeDoc.type} ids={EDITORS_PICK_IDS[activeDocId] || []} pool={activeDoc.pool} buildFn={activeDoc.build} useTemplate={useTemplate} onPreview={setPreviewItem} color={activeDoc.color} />

      {activeDocId === 'resumes' && (
        <section className="tplx-section">
          <SectionHeading title="Find your industry" subtitle="Filter the full library down to templates built for your field." />
          <CategoryPills active={activeIndustry} onSelect={(id) => { setActiveIndustry(id); setActiveStyle(null); scrollToGrid(); }} getCount={getIndustryCount} />
        </section>
      )}

      {activeDocId === 'resumes' && (
        <StyleBrowseSection activeStyle={activeStyle} onSelect={(id) => { setActiveStyle(id); setActiveIndustry('all'); scrollToGrid(); }} counts={styleCounts} />
      )}

      <div ref={gridRef}>
        {showResults ? (
          <section className="tplx-section">
            <div className="tplx-results-head">
              <div>
                <h2>{resultsTitle}</h2>
                <span>{filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'}</span>
              </div>
              <div className="tplx-results-controls">
                <div className="tplx-sort">
                  <SlidersHorizontal size={14} />
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort templates">
                    {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                {(activeIndustry !== 'all' || activeStyle || searchQuery) && (
                  <button type="button" className="tplx-clear-filters" onClick={clearFilters}><X size={13} /> Clear filters</button>
                )}
              </div>
            </div>
            {filteredTemplates.length ? (
              <>
                <CardGrid templates={filteredTemplates.slice(0, visibleCount)} docType={activeDoc.type} buildFn={activeDoc.build} useTemplate={useTemplate} onPreview={setPreviewItem} color={activeDoc.color} stagger={false} />
                {visibleCount < filteredTemplates.length && (
                  <div style={{ textAlign: 'center', marginTop: 32 }}>
                    <button type="button" className="tplx-view-all" onClick={() => setVisibleCount(v => v + 12)}>
                      Load more templates <ChevronDown size={14} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <EmptyState onClear={clearFilters} />
            )}
          </section>
        ) : (
          <div>
            {INDUSTRY_GROUPS.map(group => (
              <IndustrySection
                key={group.id}
                group={group}
                allTemplates={TEMPLATES}
                expanded={!!expandedIndustries[group.id]}
                onExpand={() => setExpandedIndustries(p => ({ ...p, [group.id]: true }))}
                docType="resume"
                buildFn={buildShowcaseResume}
                useTemplate={useTemplate}
                onPreview={setPreviewItem}
              />
            ))}
          </div>
        )}
      </div>

      <WhyChooseSection />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTASection user={user} navigate={navigate} onBrowse={scrollToGrid} />

      <MarketingFooter navigate={navigate} goTo={goTo} />

      <AnimatePresence>
        {previewItem && <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} useTemplate={useTemplate} />}
      </AnimatePresence>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const TemplatesPageStyles = () => (
  <style>{`
    .tplx-page { min-height: 100vh; background: #F8FAFC; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    @keyframes tplxHeroFloatA { 0%,100%{transform:rotate(-7deg) translateY(0)} 50%{transform:rotate(-7deg) translateY(-12px)} }
    @keyframes tplxHeroFloatB { 0%,100%{transform:rotate(0deg) translateY(0)} 50%{transform:rotate(0deg) translateY(-15px)} }
    @keyframes tplxHeroFloatC { 0%,100%{transform:rotate(7deg) translateY(0)} 50%{transform:rotate(7deg) translateY(-10px)} }
    @keyframes tplxFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }

    /* Hero */
    .tplx-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 190px; position: relative; overflow: hidden; }
    .tplx-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .tplx-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .tplx-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .tplx-hero-left { flex: 0 0 48%; }
    .tplx-hero-left h1 { margin: 18px 0 20px; font-size: 50px; line-height: 1.08; font-weight: 900; letter-spacing: -1.6px; color: white; }
    .tplx-hero-left p { margin: 0 0 32px; font-size: 17px; line-height: 1.7; color: #94A3B8; max-width: 460px; }
    .tplx-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .tplx-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; min-height: 460px; }
    .tplx-hero-stack { position: relative; width: 420px; height: 400px; }
    .tplx-hero-card-wrap { position: absolute; }
    .tplx-hero-card-wrap-0 { left: 0; top: 60px; z-index: 1; }
    .tplx-hero-card-wrap-1 { left: 90px; top: 0; z-index: 3; }
    .tplx-hero-card-wrap-2 { left: 180px; top: 46px; z-index: 2; }
    .tplx-hero-card { width: 210px; background: white; border-radius: 14px; overflow: hidden; box-shadow: 0 30px 70px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.08); }
    .tplx-hero-card-0 { animation: tplxHeroFloatA 6s ease-in-out infinite; }
    .tplx-hero-card-1 { animation: tplxHeroFloatB 6.6s ease-in-out infinite 0.25s; }
    .tplx-hero-card-2 { animation: tplxHeroFloatC 5.6s ease-in-out infinite 0.5s; }
    .tplx-hero-card-chrome { background: #F1F5F9; padding: 7px 9px; display: flex; gap: 4px; }
    .tplx-hero-card-chrome span { width: 6px; height: 6px; border-radius: 50%; background: #CBD5E1; display: inline-block; }
    .tplx-hero-card-preview { height: 240px; overflow: hidden; position: relative; background: white; }
    .tplx-float-badge { position: absolute; z-index: 5; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.9); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-radius: 12px; padding: 9px 14px 9px 9px; box-shadow: 0 16px 36px rgba(0,0,0,0.35); font-size: 12.5px; font-weight: 800; color: #111827; white-space: nowrap; animation: tplxFloat 5.5s ease-in-out infinite; }
    .tplx-float-badge-1 { top: 4px; right: -20px; animation-delay: 0.2s; }
    .tplx-float-badge-2 { bottom: 70px; left: -30px; animation-delay: 0.6s; }
    .tplx-float-badge-3 { bottom: -6px; right: 10px; animation-delay: 1s; }
    .tplx-float-icon { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .tplx-float-icon-green { background: linear-gradient(135deg,#D1FAE5,#A7F3D0); color: #059669; }
    .tplx-float-icon-blue { background: linear-gradient(135deg,#DBEAFE,#BFDBFE); color: #1D4ED8; }
    .tplx-float-icon-purple { background: linear-gradient(135deg,#F3E8FF,#E9D5FF); color: #7C3AED; }

    /* Buttons */
    .tplx-btn-primary { background: #EF1D28; color: white; border: none; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 6px 20px rgba(239,29,40,0.3); }
    .tplx-btn-primary:hover { background: #D91620; transform: translateY(-2px); box-shadow: 0 10px 28px rgba(239,29,40,0.4); }
    .tplx-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .tplx-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }
    .tplx-btn-secondary { background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 10px; padding: 11px 22px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: inherit; transition: all 0.2s; }
    .tplx-btn-secondary:hover { border-color: #CBD5E1; background: #F9FAFB; }

    /* Section heading */
    .tplx-section { padding: 64px 48px; max-width: 1180px; margin: 0 auto; }
    .tplx-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .tplx-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .tplx-sec-heading { margin-bottom: 32px; }
    .tplx-sec-heading h2 { margin: 0; font-size: 30px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .tplx-sec-heading p { margin: 10px 0 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 640px; }
    .tplx-sec-heading-center { text-align: center; }
    .tplx-sec-heading-center p { margin-left: auto; margin-right: auto; }

    /* Search */
    .tplx-search-outer { padding: 0 24px 60px; position: relative; }
    .tplx-search-card { max-width: 860px; margin: -60px auto 0; position: relative; z-index: 5; background: rgba(255,255,255,0.9); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-radius: 26px; border: 1px solid rgba(255,255,255,0.7); box-shadow: 0 30px 80px rgba(15,23,42,0.16), 0 6px 20px rgba(15,23,42,0.06); padding: 30px 36px 34px; }
    .tplx-doctype-tabs { display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap; }
    .tplx-doctype-tab { border: 1.5px solid #E5E7EB; background: white; color: #475569; border-radius: 10px; padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 7px; transition: all 0.16s; }
    .tplx-doctype-tab.active { color: white; border-color: transparent; }
    .tplx-search-bar { display: flex; align-items: center; gap: 12px; background: rgba(248,250,252,0.9); border: 1.5px solid #E2E8F0; border-radius: 999px; padding: 15px 22px; transition: all 0.25s; }
    .tplx-search-bar.focused { border-color: #EF1D28; box-shadow: 0 0 0 4px rgba(239,29,40,0.12); background: white; transform: scale(1.005); }
    .tplx-search-icon { color: #94A3B8; flex-shrink: 0; }
    .tplx-search-bar input { border: none; outline: none; background: transparent; flex: 1; font-size: 16px; font-family: inherit; color: #0F172A; min-width: 0; }
    .tplx-search-bar input::placeholder { color: #94A3B8; }
    .tplx-search-clear { border: none; background: #F1F5F9; color: #64748B; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
    .tplx-ai-suggestion { margin-top: 14px; display: inline-flex; align-items: center; gap: 7px; background: linear-gradient(135deg,#FFF1F2,#FEF2F2); border: 1px solid #FECACA; color: #DC2626; border-radius: 999px; padding: 7px 15px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; }
    .tplx-search-meta { display: flex; gap: 32px; flex-wrap: wrap; margin-top: 22px; }
    .tplx-search-block { flex: 1; min-width: 220px; }
    .tplx-search-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 800; color: #94A3B8; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px; }
    .tplx-search-chips { display: flex; flex-wrap: wrap; gap: 7px; }
    .tplx-chip { border: 1.5px solid #E5E7EB; background: white; color: #475569; border-radius: 999px; padding: 6px 13px; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; }
    .tplx-chip:hover { border-color: #EF1D28; color: #EF1D28; background: #FFF1F2; }
    .tplx-chip-recent { background: #F8FAFC; }
    .tplx-chip-clear { border-style: dashed; color: #94A3B8; }

    /* AI banner */
    .tplx-ai-banner-outer { padding: 0 24px 8px; }
    .tplx-ai-banner { position: relative; overflow: hidden; max-width: 1180px; margin: 0 auto; background: linear-gradient(135deg,#FFFFFF 0%,#FFF7F7 60%,#FFF1F2 100%); border: 1px solid #FDE0E1; box-shadow: 0 12px 32px rgba(239,29,40,0.07), 0 2px 8px rgba(15,23,42,0.03); border-radius: 20px; padding: 22px 28px; display: flex; align-items: center; gap: 28px; flex-wrap: wrap; }
    .tplx-ai-banner::before { content: ''; position: absolute; top: -60%; right: -8%; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle, rgba(239,29,40,0.1) 0%, transparent 70%); pointer-events: none; }
    .tplx-ai-banner-head { position: relative; display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
    .tplx-ai-banner-icon { width: 40px; height: 40px; border-radius: 12px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 16px rgba(239,29,40,0.32); }
    .tplx-ai-banner-title { font-size: 14px; font-weight: 800; color: #0F172A; }
    .tplx-ai-banner-sub { font-size: 12px; color: #94A3B8; margin-top: 1px; }
    .tplx-ai-banner-row { position: relative; display: flex; gap: 10px; flex-wrap: wrap; flex: 1; }
    .tplx-ai-banner-chip { display: inline-flex; align-items: center; gap: 7px; background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 999px; padding: 8px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.15s; }
    .tplx-ai-banner-chip:hover { border-color: #EF1D28; color: #EF1D28; background: #FFF1F2; }
    .tplx-ai-banner-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

    /* Pills */
    .tplx-pills-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -4px; }
    .tplx-pills-scroll::-webkit-scrollbar { display: none; }
    .tplx-pills-row { display: flex; flex-wrap: wrap; gap: 10px; padding: 4px; }
    .tplx-pill { position: relative; border: 1.5px solid #E5E7EB; background: white; border-radius: 999px; padding: 10px 18px; font-size: 13.5px; font-weight: 700; cursor: pointer; font-family: inherit; overflow: hidden; flex-shrink: 0; transition: border-color 0.16s; }
    .tplx-pill.active { border-color: transparent; }
    .tplx-pill-bg { position: absolute; inset: 0; border-radius: 999px; z-index: 0; }
    .tplx-pill-content { position: relative; z-index: 1; display: flex; align-items: center; gap: 7px; color: #374151; }
    .tplx-pill.active .tplx-pill-content { color: white; }
    .tplx-pill-count { font-size: 11px; font-weight: 800; padding: 1px 7px; border-radius: 999px; background: #F1F5F9; color: #64748B; }
    .tplx-pill.active .tplx-pill-count { background: rgba(255,255,255,0.25); color: white; }

    /* Style cards */
    .tplx-style-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 18px; }
    .tplx-style-card { text-align: left; background: white; border: 1.5px solid #E5E7EB; border-radius: 18px; padding: 22px; cursor: pointer; font-family: inherit; transition: box-shadow 0.2s, border-color 0.2s; }
    .tplx-style-card:hover { box-shadow: 0 14px 34px rgba(15,23,42,0.08); border-color: #CBD5E1; }
    .tplx-style-card.active { border-color: #EF1D28; box-shadow: 0 14px 34px rgba(239,29,40,0.12); }
    .tplx-style-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
    .tplx-style-name { font-size: 16px; font-weight: 800; color: #0F172A; margin-bottom: 4px; }
    .tplx-style-desc { font-size: 13px; color: #64748B; line-height: 1.5; margin-bottom: 12px; }
    .tplx-style-count { font-size: 12px; font-weight: 700; color: #94A3B8; }

    /* Card grid */
    .tplx-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 24px; align-items: stretch; }
    .tplx-grid-featured { gap: 28px; }
    .tplx-card { display: flex; flex-direction: column; height: 100%; background: white; border: 1px solid #E5E7EB; border-radius: 20px; overflow: hidden; box-shadow: 0 14px 40px rgba(15,23,42,0.06); transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s ease, border-color 0.28s ease; }
    .tplx-card:hover { transform: translateY(-7px); box-shadow: 0 26px 60px rgba(15,23,42,0.14); border-color: #CBD5E1; }
    .tplx-card-preview { position: relative; background: linear-gradient(180deg,#F1F5F9 0%,#E2E8F0 100%); padding: 22px 22px 0; display: flex; justify-content: center; align-items: flex-start; overflow: hidden; height: 300px; }
    .tplx-card-lg .tplx-card-preview { height: 360px; }
    .tplx-card-frame { width: min(100%, 86%); position: relative; }
    .tplx-card-frame > div { width: 100%; }
    .tplx-card-fade { position: absolute; bottom: 0; left: 0; right: 0; height: 46px; background: linear-gradient(to bottom, transparent, #E2E8F0); pointer-events: none; }
    .tplx-card-badges { position: absolute; top: 12px; left: 12px; z-index: 4; display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
    .tplx-badge { display: inline-flex; align-items: center; gap: 4px; border-radius: 6px; padding: 3px 9px; font-size: 10px; font-weight: 800; letter-spacing: 0.3px; color: white; box-shadow: 0 2px 8px rgba(0,0,0,0.18); transition: transform 0.2s ease; }
    .tplx-card:hover .tplx-badge { transform: scale(1.06); }
    .tplx-badge-ats { background: linear-gradient(135deg,#1D4ED8,#2563EB); }
    .tplx-badge-student { background: linear-gradient(135deg,#0D9488,#0F766E); }
    .tplx-badge-new { background: linear-gradient(135deg,#059669,#10B981); }
    .tplx-badge-ribbon { background: linear-gradient(135deg,#B45309,#D97706); }
    .tplx-card-overlay { position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; gap: 8px; padding: 0 16px 16px; background: linear-gradient(to top, rgba(15,23,42,0.55), transparent 55%); opacity: 0; transition: opacity 0.22s ease; pointer-events: none; }
    .tplx-card:hover .tplx-card-overlay { opacity: 1; }
    .tplx-card-btn { border: none; border-radius: 9px; padding: 10px 14px; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; pointer-events: auto; transform: translateY(14px); opacity: 0; transition: transform 0.26s cubic-bezier(0.34,1.56,0.64,1), opacity 0.22s ease, filter 0.16s ease; }
    .tplx-card:hover .tplx-card-btn { transform: translateY(0); opacity: 1; }
    .tplx-card:hover .tplx-card-btn-solid { transition-delay: 0.04s; }
    .tplx-card-btn-ghost { background: rgba(255,255,255,0.92); color: #111827; }
    .tplx-card-btn-ghost:hover { background: white; }
    .tplx-card-btn-solid { color: white; }
    .tplx-card-btn-solid:hover { filter: brightness(0.92); }
    .tplx-card-body { padding: 16px 18px 18px; display: flex; flex-direction: column; flex: 1; }
    .tplx-card-title-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
    .tplx-card-title { font-size: 15.5px; font-weight: 850; color: #111827; }
    .tplx-card-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
    .tplx-card-desc { margin: 6px 0 0; color: #64748B; font-size: 12.5px; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

    /* Scroller (editor's picks) */
    .tplx-scroller-wrap { position: relative; }
    .tplx-scroller-track { display: flex; gap: 20px; overflow-x: auto; scroll-behavior: smooth; scrollbar-width: none; -ms-overflow-style: none; padding: 4px 4px 10px; }
    .tplx-scroller-track::-webkit-scrollbar { display: none; }
    .tplx-scroller-item { flex: 0 0 260px; }
    .tplx-scroller-nav { position: absolute; top: 40%; transform: translateY(-50%); z-index: 10; width: 42px; height: 42px; border-radius: 50%; background: white; border: 1.5px solid #E2E8F0; box-shadow: 0 4px 16px rgba(0,0,0,0.12); cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; transition: all 0.18s; }
    .tplx-scroller-nav:hover { background: #0F172A; border-color: #0F172A; color: white; }
    .tplx-scroller-nav-left { left: -14px; }
    .tplx-scroller-nav-right { right: -14px; }

    /* Industry section head */
    .tplx-industry-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 22px; padding-bottom: 16px; border-bottom: 1.5px solid #E5E7EB; }
    .tplx-industry-head-left { display: flex; align-items: center; gap: 14px; }
    .tplx-industry-icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .tplx-industry-head-left h3 { margin: 0; font-size: 19px; font-weight: 850; color: #0F172A; }
    .tplx-industry-head-left span { font-size: 12.5px; color: #94A3B8; font-weight: 600; }
    .tplx-view-all { background: none; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 9px; padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px; transition: all 0.16s; }
    .tplx-view-all:hover { border-color: #EF1D28; color: #EF1D28; }

    /* Results head */
    .tplx-results-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 26px; }
    .tplx-results-head h2 { margin: 0; font-size: 24px; font-weight: 900; color: #0F172A; letter-spacing: -0.6px; }
    .tplx-results-head span { font-size: 13px; color: #94A3B8; font-weight: 600; }
    .tplx-results-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .tplx-sort { display: flex; align-items: center; gap: 8px; border: 1.5px solid #E5E7EB; background: white; border-radius: 10px; padding: 8px 14px; color: #64748B; }
    .tplx-sort select { border: none; outline: none; background: transparent; font-family: inherit; font-size: 13px; font-weight: 700; color: #374151; cursor: pointer; }
    .tplx-clear-filters { display: inline-flex; align-items: center; gap: 5px; background: #FFF1F2; border: 1.5px solid #FECACA; color: #DC2626; border-radius: 10px; padding: 8px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit; }

    /* Empty state */
    .tplx-empty { text-align: center; background: white; border: 1.5px dashed #E2E8F0; border-radius: 20px; padding: 56px 24px; }
    .tplx-empty-icon { width: 52px; height: 52px; border-radius: 14px; background: #F1F5F9; color: #94A3B8; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .tplx-empty-title { font-size: 17px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
    .tplx-empty p { color: #64748B; font-size: 14px; margin: 0 0 20px; }

    /* Why choose */
    .tplx-why-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 18px; }
    .tplx-why-card { background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 26px 22px; transition: box-shadow 0.2s; }
    .tplx-why-card:hover { box-shadow: 0 14px 34px rgba(15,23,42,0.08); }
    .tplx-why-icon { width: 44px; height: 44px; border-radius: 12px; background: #FFF1F2; color: #DC2626; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
    .tplx-why-title { font-size: 16px; font-weight: 800; color: #0F172A; margin-bottom: 8px; }
    .tplx-why-card p { margin: 0; color: #64748B; font-size: 13.5px; line-height: 1.6; }

    /* Testimonials */
    .tplx-testimonial-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 22px; }
    .tplx-testimonial { background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 26px; }
    .tplx-testimonial-quote { color: #FECACA; margin-bottom: 10px; }
    .tplx-testimonial p { margin: 12px 0 20px; color: #374151; font-size: 14.5px; line-height: 1.7; }
    .tplx-testimonial-person { display: flex; align-items: center; gap: 10px; }
    .tplx-testimonial-avatar { width: 36px; height: 36px; border-radius: 50%; color: white; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .tplx-testimonial-name { font-size: 13.5px; font-weight: 800; color: #111827; }
    .tplx-testimonial-role { font-size: 12px; color: #94A3B8; }

    /* FAQ */
    .tplx-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .tplx-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .tplx-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .tplx-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .tplx-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .tplx-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .tplx-faq-item.open .tplx-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .tplx-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .tplx-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .tplx-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .tplx-faq-item.open .tplx-faq-icon .bar { background: #EF1D28; }
    .tplx-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .tplx-cta-outer { padding: 20px 48px 96px; }
    .tplx-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 76px 48px; border: 1px solid #1A2A3E; text-align: center; }
    .tplx-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .tplx-cta-glow-1 { position: absolute; top: -120px; right: -80px; width: 500px; height: 420px; background: radial-gradient(circle, rgba(239,29,40,0.16) 0%, transparent 60%); pointer-events: none; }
    .tplx-cta-glow-2 { position: absolute; bottom: -80px; left: -60px; width: 340px; height: 340px; background: radial-gradient(circle, rgba(29,78,216,0.1) 0%, transparent 65%); pointer-events: none; }
    .tplx-cta-content { position: relative; }
    .tplx-cta-content h2 { margin: 0 0 16px; font-size: 38px; font-weight: 900; color: white; letter-spacing: -1px; line-height: 1.15; }
    .tplx-cta-content p { margin: 0 auto 32px; color: #94A3B8; font-size: 16px; line-height: 1.7; max-width: 480px; }
    .tplx-cta-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
    .tplx-cta-perks { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin-top: 26px; }
    .tplx-cta-perks span { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #6B7280; font-weight: 500; }

    /* Preview modal */
    .tplx-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(10,15,25,0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 24px; }
    .tplx-modal-card { background: white; border-radius: 22px; width: 100%; max-width: 640px; max-height: 88vh; overflow: hidden; display: flex; flex-direction: column; position: relative; box-shadow: 0 40px 100px rgba(0,0,0,0.4); }
    .tplx-modal-close { position: absolute; top: 14px; right: 14px; z-index: 5; border: none; background: rgba(255,255,255,0.9); color: #374151; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,0.15); }
    .tplx-modal-preview { background: #F1F5F9; overflow: auto; flex: 1; display: flex; justify-content: center; padding: 24px; }
    .tplx-modal-preview-inner { transform: scale(0.62); }
    .tplx-modal-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 22px; border-top: 1px solid #F1F5F9; flex-wrap: wrap; }
    .tplx-modal-name { font-size: 16px; font-weight: 800; color: #111827; }
    .tplx-modal-desc { font-size: 13px; color: #64748B; margin-top: 2px; }
    .tplx-modal-use { border: none; color: white; border-radius: 10px; padding: 12px 20px; font-size: 13.5px; font-weight: 800; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 7px; flex-shrink: 0; }

    /* Responsive */
    @media (max-width: 1100px) {
      .tplx-grid, .tplx-grid-featured { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .tplx-why-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .tplx-testimonial-grid { grid-template-columns: 1fr; }
      .tplx-style-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .tplx-page section { padding-left: 24px; padding-right: 24px; }
      .tplx-hero { padding: 64px 24px 100px; }
      .tplx-hero-inner { flex-direction: column; gap: 48px; text-align: center; }
      .tplx-hero-left { flex: none; }
      .tplx-hero-left p { margin-left: auto; margin-right: auto; }
      .tplx-hero-ctas { justify-content: center; }
      .tplx-hero-left h1 { font-size: 40px; }
      .tplx-hero-right { min-height: 380px; }
      .tplx-section { padding: 48px 24px; }
      .tplx-section-tint { padding-left: 24px; padding-right: 24px; }
      .tplx-pills-row { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; }
    }
    @media (max-width: 640px) {
      .tplx-grid, .tplx-grid-featured { grid-template-columns: 1fr; }
      .tplx-why-grid { grid-template-columns: 1fr; }
      .tplx-style-grid { grid-template-columns: 1fr; }
      .tplx-hero-left h1 { font-size: 32px; letter-spacing: -1px; }
      .tplx-search-card { padding: 22px 18px 26px; border-radius: 20px; margin-top: -40px; }
      .tplx-search-bar { padding: 13px 16px; }
      .tplx-hero-right { display: none; }
      .tplx-faq-trigger span:first-child { font-size: 14.5px; }
      .tplx-faq-trigger { padding: 16px 18px; }
      .tplx-faq-answer { padding: 0 18px 18px; }
      .tplx-cta-content h2 { font-size: 27px; }
      .tplx-cta-card { padding: 48px 24px; }
      .tplx-ai-banner { flex-direction: column; align-items: flex-start; }
    }
  `}</style>
);
